import React, { useCallback, useEffect, useLayoutEffect } from 'react';
import Animated from 'react-native-reanimated';
import { TouchableOpacity } from 'react-native';
import { AppView, Button, ButtonGroup, CardView, rem, SCROLL_INDICATOR_INSETS, withAnimatedNavigationHeader, } from 'design-system';
import InfoCardWithIndex from 'design-system/InfoCardWithIndex';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import Text from 'design-system/Text';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import FormattedAddress from 'features/invest/components/FormattedAddress';
import { InfoModal } from 'features/invest/components/InfoModal';
import { ProductTransferDisplayLongMap, ProductTransferDisplayShortMap } from 'features/invest/constants';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import { useUserAddress } from 'hooks/useUserAddress';
import createStyleSheets from 'utils/createStyleSheets';
import Modal from 'utils/packages/Modal';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { HEIGHT, HEIGHT_PLUS_PADDING } from 'design-system/Button';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import EmptyComponent from 'design-system/EmptyComponent';
import Amplitude from 'utils/amplitude';
import { selectTradingAccountFromId } from '../reducers/selectors';
import { transferProductLater } from '../actions';
import { amplitudeSource } from '../utils/analytics';
import { isWeb } from '../../../constants';
const strings = {
    title: (t) => t === 'ISA' ? 'Transfer your existing ISA' : `How we find and transfer your ${ProductTransferDisplayLongMap[t]}`,
    subTitle: 'Keep your money tax-free while we take care of contacting your provider',
    step1Title: (t) => `Tell us about your ${ProductTransferDisplayShortMap[t]}`,
    step1Description: (t) => `Share the account details for the ${t} you want to transfer.`,
    step2Title: 'We contact your provider',
    step2Description: `We’ll reach out to request the transfer and keep things moving. Most transfers complete in 4–6 weeks, but can take longer.`,
    whatWillINeed: 'What will I need?',
    whatWillINeedDescription: (t) => `To transfer your ${t === 'JISA' ? ProductTransferDisplayLongMap[t] : t}, you’ll need${t === 'JISA' ? '' : ' your'} provider name, account reference, address, and an estimated amount.`,
    addressModalTitle: {
        PENDING: 'Your address is being verified',
        FAIL: 'Your address couldn’t be verified',
    },
    addressModalSubtitle: {
        PENDING: 'We’re currently verifying your new address. You’ll be able to transfer an ISA once verification is complete.',
        FAIL: 'There was an issue verifying your address. You’ll need to update it before transferring an ISA.',
    },
    verificationPending: 'Verification pending',
    verificationFailed: 'Rejected',
    changeAddress: 'Change address',
    updateAddress: 'Update address',
    continue: 'Continue',
    okay: 'Okay',
    dismiss: 'Later',
};
const AddressModalContent = ({ subtitle, address: primaryAddress, }) => {
    const styles = useStyles(styleSet);
    return (<>
      <Text TextThin-14 Secondary>
        {subtitle}
      </Text>
      <CardView withPadding noMargin style={styles.addressModalContent}>
        <FormattedAddress {...primaryAddress} uk/>
      </CardView>
    </>);
};
const ProductTransferIntroScreen = withAnimatedNavigationHeader(({ navigation, route, scrollHandler }) => {
    const colors = useColors();
    const dispatch = useAppDispatch();
    const { accountId, showDismiss } = route.params || {};
    const jisaAccount = useAppSelector((state) => selectTradingAccountFromId(state, accountId));
    const accountType = accountId ? 'JISA' : 'ISA';
    useEffect(() => {
        Amplitude.logEvent('Transfer Account Identification Screen', {
            source: amplitudeSource,
            account: accountId ? 'jisa' : 'isa',
        });
    }, [accountId]);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentBottomPadding = showDismiss ? HEIGHT_PLUS_PADDING + HEIGHT + rem(8) : HEIGHT_PLUS_PADDING;
    const contentContainerStyle = useMarginBottom('paddingBottom', contentBottomPadding, paddingHorizontalStyle);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => <HelpScreenButton />,
            headerTintColor: colors.text.primary,
            ...(showDismiss && {
                gestureEnabled: false,
                headerLeft: () => <EmptyComponent />,
            }),
        });
    }, [colors.text.primary, showDismiss]);
    const { primaryAddress } = useUserAddress();
    const kycStatus = primaryAddress?.kycStatus;
    const handleContinue = useCallback(() => {
        if (accountType === 'ISA') {
            if (!kycStatus)
                return;
            if (kycStatus === 'PENDING' || kycStatus === 'FAIL') {
                const title = strings.addressModalTitle[kycStatus];
                const subtitle = strings.addressModalSubtitle[kycStatus];
                const changeBtnText = kycStatus === 'PENDING' ? strings.changeAddress : strings.updateAddress;
                Modal.showConfirmation({
                    title,
                    subtitle: <AddressModalContent subtitle={subtitle} address={primaryAddress}/>,
                    cancelButtonTitle: strings.okay,
                    confirmButtonTitle: changeBtnText,
                    onPressCancel: navigation.goBack,
                    onConfirm: () => {
                        navigation.navigate('ChangeAddressProgressScreen', { flow: 'CHANGE' });
                    },
                });
                return;
            }
            navigation.navigate('ProductTransferReviewAddress', {
                address: primaryAddress,
            });
            return;
        }
        if (jisaAccount && 'child' in jisaAccount) {
            navigation.navigate('ProductTransferReviewAddress', {
                accountId,
                address: jisaAccount.child.address,
            });
        }
    }, [accountType, jisaAccount, kycStatus, primaryAddress, accountId]);
    const handleWhatWillINeed = useCallback(() => {
        InfoModal.show(strings.whatWillINeed, strings.whatWillINeedDescription(accountType));
    }, [accountType]);
    const handleDismiss = useCallback(() => {
        dispatch(transferProductLater());
        navigation.pop();
    }, []);
    return (<AppView withFrame={false}>
        <Animated.ScrollView onScroll={scrollHandler} scrollEventThrottle={16} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} contentContainerStyle={contentContainerStyle}>
          <ScreenInfoHeader title={strings.title(accountType)} subTitle={strings.subTitle}/>
          <InfoCardWithIndex index="1" title={strings.step1Title(accountType)}>
            <Text TextThin-14 Secondary>
              {strings.step1Description(accountType)}
            </Text>
            <TouchableOpacity onPress={handleWhatWillINeed}>
              <Text Text-14 Primary>
                {strings.whatWillINeed}
              </Text>
            </TouchableOpacity>
          </InfoCardWithIndex>
          <InfoCardWithIndex index="2" title={strings.step2Title} subTitle={strings.step2Description}/>
        </Animated.ScrollView>
        <ButtonGroup type="floating">
          {!!showDismiss && (<Button brandReversed title={strings.dismiss} onPress={handleDismiss} isFetching={!kycStatus}/>)}
          <Button brand title={strings.continue} onPress={handleContinue} isFetching={!kycStatus}/>
        </ButtonGroup>
      </AppView>);
}, ({ colors }) => ({
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
const styleSet = createStyleSheets((colors) => ({
    addressModalContent: {
        width: '100%',
        marginTop: rem(16),
        backgroundColor: colors.background.dark,
    },
    status: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: rem(8),
    },
}));
export default ProductTransferIntroScreen;
